<script>
  import { useUrlParams } from "@stores/urlStore.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { DataTable, MultiSelect} from "carbon-components-svelte";
  import {
  Pagination,
    Select,
    SelectItem,
    TextInput,
    Button,
  } from "paxray-components-svelte";
  import { escapeRegExp } from "lib/util/regex.js";
  import { EDIT_ICON } from "@utils/icons.js";

  const urlParams = useUrlParams();

  export let currentAnonymizationRule = {};
  export let modalOpen = false;
  export let projectStep = 2; // Default to step 2 for anonymization rules

  let uniqueDataApplicationNames = [];
  let uniquePreviews = [];
  let preprocessing = false;
  let applications = [];
  let filteredRowIdsData = [];
  let teamids = [];
  let teamlist = [];
  let selectedOption = "all";

  let appfilter = {
    applicationname: "",
    windowtitle: "",
    count: "",
  };
  let pageData;
  let pageSizeData;
  let fullApplicationsList = [];

  $: ({ idproject } = $urlParams);

  $: {
    if (appfilter || teamids || selectedOption) filterAppList();
  }

  async function getApplications() {
    const res = await fetchWithSession(
      `/api/damprojects/getAnonymizationLog?idproject=${idproject}&projectStep=${projectStep}`,
    );
    const text = await res.json();
    fullApplicationsList = text;
    applications = text;
    filteredRowIdsData = applications.map((item) => item.id);
    const allTeams = text.flatMap((item) => item.teamlist);
    const uniqueTeams = allTeams.filter(
      (team, index, self) => index === self.findIndex((t) => t.id === team.id),
    );
    teamlist = uniqueTeams.map((team) => ({
      id: team.id,
      text: team.name,
    }));
    uniqueDataApplicationNames = [
      ...new Set(applications.map((app) => app.applicationname.trim())),
    ]
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map((name, index) => ({ id: index.toString(), key: name }));
    uniquePreviews = [
      ...new Set(applications.map((app) => app.windowtitle.trim())),
    ]
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map((name, index) => ({ id: index.toString(), key: name }));
    if (res.ok) {
      return text;
    } else {
      throw new Error(text);
    }
  }

  function filterAppList() {
    applications = fullApplicationsList.filter((item) => {
      let matches = true;
      if (appfilter.applicationname != "*") {
        matches =
          matches &&
          item.applicationname
            .toLowerCase()
            .includes(appfilter.applicationname.toLowerCase());
      }
      if (appfilter.windowtitle != "*") {
        matches =
          matches &&
          item.preview
            .toLowerCase()
            .includes(appfilter.windowtitle.toLowerCase());
      }
      if (Array.isArray(teamids) && teamids.length > 0) {
        const teamIds = item.teamlist.map((team) => team.id);
        const hasMatchingTeam = teamIds.some((id) => teamids.includes(id));
        matches = matches && hasMatchingTeam;
      }
      // Filter by recording option
      if (selectedOption === "in_task_recording") {
        matches = matches && !!item.in_task_recording;
      } else if (selectedOption === "no_recording") {
        matches = matches && !item.in_task_recording;
      }
      return matches;
    });
    filteredRowIdsData = applications.map((item) => item.id);
  }

  function newRuleFromEntry(entry) {
    if (!entry) {
      entry = { applicationname: "", preview: "" };
    }
    currentAnonymizationRule = {
      id: 0,
      applicationname: entry.applicationname,
      name: "",
      search: escapeRegExp(entry.preview),
      replace: entry.preview,
    };
    modalOpen = true;
  }

  function textInputKeyDown(event) {
    if (event.key === "ArrowUp") {
      previousApplication();
    } else if (event.key === "ArrowDown") {
      nextApplication();
    }
  }

  function previousApplication() {
    let index = uniqueDataApplicationNames.findIndex(
      (item) => item.key === appfilter.applicationname,
    );
    index === 0
      ? (appfilter.applicationname =
          uniqueDataApplicationNames[uniqueDataApplicationNames.length - 1].key)
      : (appfilter.applicationname = uniqueDataApplicationNames[index - 1].key);
  }

  function nextApplication() {
    let index = uniqueDataApplicationNames.findIndex(
      (item) => item.key === appfilter.applicationname,
    );
    index === uniqueDataApplicationNames.length - 1
      ? (appfilter.applicationname = uniqueDataApplicationNames[0].key)
      : (appfilter.applicationname = uniqueDataApplicationNames[index + 1].key);
  }

  async function applyRules() {
    await getApplications().then(() => {
      filterAppList();
    });
  }

  $: if (idproject) getApplications();
</script>

<div class="filter_wrapper">
  <div class="filter_item">
    <MultiSelect
      titleText="Teams"
      bind:selectedIds={teamids}
      label="Select a team ..."
      items={teamlist}
    />
  </div>
  <div class="filter_item">
    <Select labelText="Recording Option" bind:selected={selectedOption}>
      <SelectItem value="all" text="Show all" />
      <SelectItem value="in_task_recording" text="In Task Recording" />
      <SelectItem value="no_recording" text="No Recording" />
    </Select>
  </div>
</div>

<table>
  <tr>
    <td>
      <TextInput
        labelText="Application"
        size="sm"
        bind:value={appfilter.applicationname}
        placeholder="All Applications"
        list="Application"
        on:change={filterAppList}
        on:keydown={textInputKeyDown}
      />

      <datalist id="Application">
        {#each uniqueDataApplicationNames.sort() as application}
          {#if application.key !== appfilter.applicationname}
            <option value={application.key} />
          {/if}
        {/each}
      </datalist>
    </td>
    <td class="selection_button">
      <div
        class="icon chevron_button"
        on:mousedown={() => {
          previousApplication();
        }}
      >
        <img
          class="flipped"
          src="/img/assistant/icons/chevron_down_dark.svg"
          alt="Previous"
        />
      </div>
    </td>
    <td class="selection_button">
      <div
        class="icon chevron_button"
        on:mousedown={() => {
          nextApplication();
        }}
      >
        <img src="/img/assistant/icons/chevron_down_dark.svg" alt="Next" />
      </div>
    </td>
    <td>
      <TextInput
        labelText="Preview"
        size="sm"
        bind:value={appfilter.windowtitle}
        placeholder="All Previews"
        list="Preview"
        on:change={filterAppList}
      />
      <datalist id="Preview">
        {#each uniquePreviews as application}
          <option value={application.key} />
        {/each}
      </datalist>
    </td>
    <td class="button_list" style="padding-right: 0.5rem;">
      <Button
        text="Add Rule"
        kind="secondary"
        loading={preprocessing}
        on:click={() => {
          newRuleFromEntry(false);
        }}
      />
    </td>

    <td class="button_list">
      <Button
        text="Apply Rules"
        kind="secondary"
        loading={preprocessing}
        on:click={applyRules}
      />
    </td>
  </tr>
</table>

<DataTable
  size="short"
  headers={[
    { key: "applicationname", value: "Application" },
    { key: "windowtitle", value: "Window Title" },
    { key: "preview", value: "Preview" },
    { key: "unique", value: "Unique", width: "100px" },
    { key: "count", value: "Count", width: "100px" },
    { key: "menu", empty: true, width: "80px" },
  ]}
  rows={applications}
  sortable
  page={pageData}
  pageSize={pageSizeData}
>
  <svelte:fragment slot="cell" let:cell let:row>
    {#if cell.key === "menu"}
      <Button
        size="small"
        kind="secondary"
        icon={EDIT_ICON}
        on:click={() => {
          newRuleFromEntry(row);
        }}
        iconDescription="Create Rule From Entry"
      />
    {:else if cell.key !== "menu"}
      {cell.value}
    {/if}
  </svelte:fragment>
</DataTable>
<Pagination
  totalItems={filteredRowIdsData.length}
  pageSizes={[10, 25, 50, 100]}
  bind:page={pageData}
  bind:pageSize={pageSizeData}
/>

<style>
  :global(
      .bx--table-toolbar .bx--toolbar-search-container-active,
      .bx--table-toolbar .bx--toolbar-search-container-persistent
    ) {
    flex: auto;
    transition: flex 175ms cubic-bezier(0.2, 0, 0.38, 0.9);
  }
  :global(
      .bx--table-toolbar .bx--toolbar-search-container-active .bx--search-input,
      .bx--table-toolbar
        .bx--toolbar-search-container-persistent
        .bx--search-input
    ) {
    visibility: inherit;
  }
  :global(
      .bx--table-toolbar
        .bx--toolbar-search-container-active
        .bx--search-input:focus,
      .bx--table-toolbar
        .bx--toolbar-search-container-persistent
        .bx--search-input:focus
    ) {
    outline: 2px solid #0f62fe;
    outline-offset: -2px;
    background-color: #e5e5e5;
  }
  /* .inline_button {
    display: flex;
    align-items: center;
  } */

  .button_list {
    vertical-align: bottom;
    white-space: nowrap;
    margin: 0;
    padding: 0;
    width: 1%;
  }

  .selection_button {
    vertical-align: bottom;
  }

  .chevron_button {
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: 1px solid var(--cds-ui-04, #8d8d8d);
    background-color: var(--cds-field-01, #f4f4f4);
    height: 2rem;
  }
  .flipped {
    transform: rotate(180deg);
  }

  .chevron_button:hover {
    background-color: #e0e0e0;
  }

  .chevron_button:active {
    background-color: #cacaca;
  }

  .button_list {
    vertical-align: bottom;
    white-space: nowrap;
    margin: 0;
    padding: 0;
    width: 1%;
  }

  .selection_button {
    vertical-align: bottom;
  }

  .chevron_button {
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: 1px solid var(--cds-ui-04, #8d8d8d);
    background-color: var(--cds-field-01, #f4f4f4);
    height: 2rem;
  }
  .flipped {
    transform: rotate(180deg);
  }

  .chevron_button:hover {
    background-color: #e0e0e0;
  }

  .chevron_button:active {
    background-color: #cacaca;
  }

  .filter_wrapper {
    margin-bottom: 1rem;
    display: flex;
    gap: 1rem;
  }

  .filter_item {
    flex: 1;
  }
</style>
